import type { AccessPolicy, AccessContext, AccessDecision } from './types';

/**
 * Pure function that evaluates whether access to a route should be allowed.
 *
 * Check order: auth -> actor role -> subscription -> setup -> shop permissions
 *
 * This function has no React dependencies and is easily unit-testable.
 */
export const evaluateAccess = (
  policy: AccessPolicy,
  context: AccessContext,
): AccessDecision => {
  // 1. Authentication check
  if (policy.auth === 'authenticated' && !context.isAuthenticated) {
    return { type: 'redirect', path: '/', reason: 'not_authenticated' };
  }

  // Public routes always allowed
  if (policy.auth === 'public') {
    return { type: 'allow' };
  }

  // 2. Actor role check
  if (policy.actors && policy.actors.length > 0) {
    if (!context.role || !policy.actors.includes(context.role)) {
      return {
        type: 'redirect',
        path: '/post-login',
        reason: `role_mismatch: required=${policy.actors.join(',')}, actual=${context.role ?? 'none'}`,
      };
    }
  }

  // 3. Subscription check
  if (policy.subscription && policy.subscription !== 'not_required') {
    const isActive = context.subscriptionStatus === 'active' || context.subscriptionStatus === 'trialing';

    if (!isActive) {
      if (policy.subscription === 'solo_required') {
        return {
          type: 'redirect',
          path: '/?view=providers',
          reason: 'solo_subscription_required',
        };
      }

      if (policy.subscription === 'shop_required') {
        // Only shop owners can access billing to fix subscription
        if (context.role === 'shop_owner' && context.shopSlug) {
          return {
            type: 'redirect',
            path: `/shops/${context.shopSlug}/billing`,
            reason: 'shop_subscription_expired_owner',
          };
        }
        return {
          type: 'redirect',
          path: '/post-login',
          reason: 'shop_subscription_required',
        };
      }
    }
  }

  // 4. Setup check
  if (policy.setup && policy.setup !== 'not_required') {
    if (!context.setupComplete) {
      if (policy.setup === 'solo_required') {
        return {
          type: 'redirect',
          path: '/provider/independent-setup',
          reason: 'solo_setup_incomplete',
        };
      }

      if (policy.setup === 'shop_required') {
        return {
          type: 'redirect',
          path: '/provider/setup',
          reason: 'shop_setup_incomplete',
        };
      }

      if (policy.setup === 'staff_required') {
        return {
          type: 'redirect',
          path: '/provider/staff-onboarding',
          reason: 'staff_setup_incomplete',
        };
      }
    }
  }

  // 5. Shop permissions check
  if (policy.shopPermissions && policy.shopPermissions.length > 0) {
    const userPermissions = context.shopPermissions || [];
    const missingPermissions = policy.shopPermissions.filter(p => !userPermissions.includes(p));

    if (missingPermissions.length > 0) {
      return {
        type: 'forbidden',
        reason: `missing_permissions: ${missingPermissions.join(',')}`,
      };
    }
  }

  return { type: 'allow' };
};
